import { Component, type ErrorInfo, type ReactNode } from "react";
import { EmptyState } from "../components.js";

/**
 * Keeps a failing screen from taking the whole app with it.
 *
 * On 2026-08-20 a single mistyped API response shape made `AssessmentHistory` call `.map` on an
 * object. React unmounted the ENTIRE tree, so the operator saw a blank page with no navigation and
 * no way back — a small bug that presented as a total outage. Without a boundary, every screen is
 * one bad payload away from that.
 *
 * Placed INSIDE the Shell, around the routed outlet only, so the top bar and footer survive: the
 * failure is contained to the panel that failed, and the user can still navigate away. That is the
 * same honest-degradation rule the data layer follows — report the part that is broken, keep the
 * part that works, never present a blank as if nothing were wrong.
 */
interface Props {
  readonly children: ReactNode;
  /** Resets the boundary when it changes — a new route gets a fresh attempt. */
  readonly resetKey?: string;
}

interface State {
  readonly error: Error | null;
  readonly resetKey?: string;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  /** Navigating away clears the failure, so one broken screen does not poison the rest. */
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console is the only sink the browser bundle has; a real error-reporting hook is a later
    // story. Logged rather than swallowed — a silent boundary is its own kind of dishonesty.
    console.error("[senesight] screen failed to render", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <>
          <div className="head">
            <h1>This screen didn&rsquo;t load</h1>
          </div>
          <EmptyState
            message={`Something went wrong rendering this page, so it has been stopped rather than shown half-built: ${this.state.error.message}. The rest of the app still works — use the navigation above to move on.`}
          />
        </>
      );
    }
    return this.props.children;
  }
}
